'use client';

import { isValidElement, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { HelpTip } from '../HelpTip';
import { ClampedDescription, ClampedHeading } from './header-text';

export type PageHeaderSize = 'display' | 'heading';
export type PageHeaderDescriptionMode = 'inline' | 'info';

export interface PageHeaderProps {
  title: ReactNode;
  /** Record avatar/identity beside title + description; replaces the module glyph.
   * Decorative initials belong in an aria-hidden element supplied by the caller. */
  leading?: ReactNode;
  /** Status or record metadata beside, never inside, the h1 accessible name. */
  titleMeta?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: ReactNode;
  tabs?: ReactNode;
  /**
   * Whether to render the bottom divider (`pb-4` + `border-b`). Default `true`.
   * Set `false` when a parent shell already owns the boundary — e.g. ListPage wraps
   * PageHeader in a bordered band, so the header's own border would double up
   * (see the double-divider fix, DESIGN.md Part IV).
   */
  divider?: boolean;
  /**
   * Page-title size (Session D title-size contract).
   * - `'heading'` (default) — `text-heading-1` (24px, weight 700 via
   *   `--font-weight-display`): operator/data screens. Typography v4 PR 3 (#12011)
   *   promoted this from `text-heading-2` (20px) per Spike B's selected recipe, so that
   *   title, section heading and body have visibly distinct jobs (§5.1 P1).
   *   **This is no longer a zero-resize swap**: adopting PageHeader on a screen whose
   *   title was hand-written as `text-heading-2` moves it up one rank, and a sibling
   *   element that hardcoded `text-heading-2` to match will now sit a rank BELOW the
   *   title. `AppointmentsLocationBreadcrumb` was exactly that case and is asserted
   *   against regression in its own smoke test.
   * - `'display'` — `text-display` (30px): dashboards and overview pages that earn the
   *   extra presence. Unchanged by PR 3; it still opts out of the heading rank.
   */
  size?: PageHeaderSize;
  /**
   * Wayfinding icon matching the current route's sidebar-nav entry (see
   * ADR-0116's Amendment, 2026-07-18). Resolved by the page-shell archetypes
   * via `useActiveNavIdentity()` — PageHeader itself never resolves this; it
   * only renders what it's given, so it stays composable/testable without a
   * router. Renders nothing unless `navColorVar` is also passed.
   */
  navIcon?: LucideIcon | undefined;
  /** The `--color-module-*` (or `--color-ai`) CSS var to color `navIcon` with. */
  navColorVar?: string | undefined;
  /**
   * How `description` renders. `'inline'` (default) -- the `<p>` below the title,
   * clamped to two lines under a 68ch measure with a measured "More" toggle when
   * it is longer (H6, #15269). `'info'` -- description shows
   * only on hover/click/tap of a small "i" affordance next to the title
   * (built on `HelpTip` with `hoverToOpen`); nothing is visible until the
   * user asks for it. A deliberate per-page choice, not a blanket default --
   * most pages should keep `'inline'`.
   */
  descriptionMode?: PageHeaderDescriptionMode;
  className?: string;
}

/**
 * Neutral page header. Per the nav-only color doctrine (ADR — page-layout-vibrancy
 * PR-1b, 2026-06-30) the page CHROME carries NO module hue: no per-page underline,
 * no module icon tile. Module hue lives in the sidebar nav only; page content is
 * vivid via categorical-by-metric color (`--color-data-*`, StatCard, CategoryChip).
 * The former `module`/`icon` props and the `ActiveModule` auto-wiring are removed.
 */
export function PageHeader({
  title,
  leading,
  titleMeta,
  description,
  actions,
  breadcrumbs,
  tabs,
  divider = true,
  size = 'heading',
  navIcon,
  navColorVar,
  descriptionMode = 'inline',
  className,
}: PageHeaderProps) {
  const NavIcon = navIcon;
  const showNavIcon = !leading && Boolean(NavIcon) && Boolean(navColorVar);
  const showInfoDescription = Boolean(description) && descriptionMode === 'info';
  const showInlineDescription = Boolean(description) && descriptionMode === 'inline';
  // The title block yields before the actions wrap (#16194, S6). In a wrapping row an item's line-break size is its
  // flex-basis, which was its max-content, and an inline description (up to 68ch) inflates that, so the actions
  // dropped onto their own line at 768-1280px although the block could narrow. Only a header with an inline
  // description AND actions changes, and only from `sm` up (below it the actions row is its own line by design).
  // Any other header keeps the content-sized block it always had, so a long title (which inflates the block the
  // same way) can still push the actions below it; that case is not addressed here.
  // The floor is FIXED, not content-sized: it does not depend on the description's "More" toggle, which a basis
  // such as min-content does, and then the wrap decision oscillates. 12rem is the narrowest the title may get
  // beside the actions (a longer title is clamped, with its tooltip).
  // Left alone: a record header (`leading` or `titleMeta`), whose title is a record's name, user data that is not
  // reliably short: squeezing it clamps the name and drops the status badge under it (measured on Customer 360 by
  // the independent judge); and a title that is not a string, which has no tooltip when clamped, so a badge or a
  // number inside it would be clipped silently. Those headers keep wrapping the actions below the title.
  // Complete literals, never interpolated next to `${}` (check-tailwind-class-interpolation).
  const yieldsToActions =
    showInlineDescription &&
    Boolean(actions) &&
    typeof title === 'string' &&
    !leading &&
    !titleMeta;
  const titleBlockClass = ['min-w-0', yieldsToActions && 'sm:grow sm:basis-48']
    .filter(Boolean)
    .join(' ');
  const titleContent = (
    <div className={titleBlockClass}>
      <div
        className={['flex items-center gap-2', titleMeta && 'flex-wrap'].filter(Boolean).join(' ')}
      >
        <ClampedHeading
          title={title}
          // The title is clamped (H5, #15269 S6: two lines below `sm`, one from `sm` up) with its full
          // text in a tooltip only when clamped; see header-text.tsx. The nav icon sits beside the
          // clamped text, not inside it.
          icon={
            showNavIcon && NavIcon ? (
              <NavIcon
                data-testid="ident-icon"
                className="h-5 w-5 shrink-0"
                style={{ color: `var(${navColorVar}-vivid, var(${navColorVar}))` }}
                aria-hidden="true"
              />
            ) : undefined
          }
          // Typography v4 PR 3 (#12011), Spike B selected recipe: the page title
          // moves up one rank, `text-heading-2` → `text-heading-1`, so title,
          // section heading and body have visibly distinct jobs (§5.1 P1).
          // `display` is unchanged and still opts out of the rank.
          //
          // Written as complete literals joined with the repo's array pattern
          // rather than a template literal. #12140: Tailwind extracts candidates
          // from raw source TEXT, so a utility adjacent to `${...}` is read as one
          // invalid candidate and the rule is silently never emitted — that is how
          // `sm:table` vanished from the production stylesheet and every DataTable
          // rendered display:none. `scripts/check-tailwind-class-interpolation.cjs`
          // guards the boundary; this is the fix shape it prescribes.
          className={[
            size === 'display' ? 'text-display' : 'text-heading-1',
            'text-primary',
            (leading || titleMeta) && 'min-w-0 break-words',
          ]
            .filter(Boolean)
            .join(' ')}
        />
        {titleMeta && <div className="shrink-0">{titleMeta}</div>}
        {showInfoDescription && (
          // Deliberately a SIBLING of <h1>, never nested inside it: the accname
          // algorithm folds a descendant element's own aria-label into an
          // ancestor heading's computed accessible name, so nesting this
          // button in the h1 would announce "About this page" as part of the
          // page's heading to screen readers. Sibling placement keeps it
          // visually adjacent (same flex row) without polluting the heading.
          <HelpTip label="About this page" trigger="i" hoverToOpen className="align-middle">
            {description}
          </HelpTip>
        )}
      </div>
      {showInlineDescription && (
        // Typography v4 PR 3 (#12011): the page description is explanatory prose,
        // not a table cell, so it takes the new `text-body-reading` role (15/24).
        // Table cells stay on body 14/22 and body-sm 12/20 so density holds.
        // H6 (#15269 S6): clamped to two lines under a 68ch measure, with a measured "More" toggle.
        <ClampedDescription
          className="text-body-reading text-secondary"
          toggleClassName="text-body-reading"
        >
          {description}
        </ClampedDescription>
      )}
    </div>
  );
  return (
    <header
      className={['flex flex-col gap-4', divider && 'pb-4 border-b border-border', className]
        .filter(Boolean)
        .join(' ')}
    >
      {breadcrumbs && <nav aria-label="Breadcrumb">{breadcrumbs}</nav>}
      <div className="flex flex-wrap items-start justify-between gap-4">
        {leading ? (
          <div className="flex min-w-0 max-w-full items-center gap-3">
            <div className="shrink-0">{leading}</div>
            {titleContent}
          </div>
        ) : (
          titleContent
        )}
        {actions &&
          // A `PageActions` is the title row's own trailing item, so its row can take the full width
          // below `sm` (H7, #16194); any other node keeps the plain wrapper it always had.
          (isValidElement(actions) &&
          (actions.type as { isPageActions?: boolean }).isPageActions === true ? (
            actions
          ) : (
            <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>
          ))}
      </div>
      {tabs}
    </header>
  );
}
